import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";

import {
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  IconButton,
  Stack,
  Paper
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { buttonRed, pcolor, rcolor } from "../style2";
import { getApiUrl } from "../services/api_service";
import ConfirmDialog from "./shared/ConfirmDialog";
import dayjs from "dayjs";

// PatientFormAssignmentList: Shows assignments inline in a table and an 'Assign' button
export default function PatientFormAssignmentList({ formId, referralId }) {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assigneeEmail, setAssigneeEmail] = useState("");
  const [assigneeName, setAssigneeName] = useState("");
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignError, setAssignError] = useState(null);
  const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadAssignments = () => {
    setLoading(true);
    setError(null);
    const authTokens = localStorage.getItem("authTokens");
    let token;
    if (authTokens) {
      const parsedAuthTokens = JSON.parse(authTokens);
      token = parsedAuthTokens?.accessToken;
    }
    if (!token) {
      setError("Unauthorized. No token available.");
      setLoading(false);
      return;
    }
    fetch(`${getApiUrl()}/PatientForm/assignments/${formId}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res) => {
        if (!res.ok) throw new Error("Failed to load assignments");
        return res.json();
      })
      .then((data) => {
        setAssignments(data || []);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  };

  // Load assignments on mount
  useEffect(() => {
    loadAssignments();
  }, [formId]);

  const handleRemoveClick = (id) => {
    setRemovingId(id);
    setRemoveConfirmOpen(true);
  };

  const handleRemoveConfirm = async () => {
    setRemoveConfirmOpen(false);
    setDeleting(true);
    const authTokens = localStorage.getItem("authTokens");
    let token;
    if (authTokens) {
      const parsedAuthTokens = JSON.parse(authTokens);
      token = parsedAuthTokens?.accessToken;
    }
    if (!token) {
      setError("Unauthorized. No token available.");
      setDeleting(false);
      return;
    }
    try {
      const res = await fetch(`${getApiUrl()}/patientform/removeassignment/${removingId}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      if (!res.ok) throw new Error("Failed to remove assignment");
      loadAssignments();
    } catch (err) {
      setError(err.message);
    } finally {
      setRemovingId(null);
      setDeleting(false);
    }
  };

  const handleAssignClick = () => {
    setAssignDialogOpen(true);
    setAssigneeEmail("");
    setAssigneeName("");
    setAssignError(null);
  };

  const handleAssignDialogClose = () => {
    setAssignDialogOpen(false);
    setAssigneeEmail("");
    setAssigneeName("");
    setAssignError(null);
  };

  const handleAssignSubmit = async () => {
    setAssignLoading(true);
    setAssignError(null);
    const authTokens = localStorage.getItem("authTokens");
    let token;
    if (authTokens) {
      const parsedAuthTokens = JSON.parse(authTokens);
      token = parsedAuthTokens?.accessToken;
    }
    if (!token) {
      setAssignError("Unauthorized. No token available.");
      setAssignLoading(false);
      return;
    }
    const payload = {
      patientFormId: formId,
      patientReferralId: referralId ?? null,
      assigneeEmail: assigneeEmail,
      assigneeName: assigneeName,
    };
    try {
      const res = await fetch(`${getApiUrl()}/patientform/addassignment`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        let errMsg = "Failed to assign form";
        try {
          const errBody = await res.json();
          if (errBody?.error) errMsg = errBody.error;
        } catch {}
        throw new Error(errMsg);
      }
      setAssignDialogOpen(false);
      setAssignLoading(false);
      loadAssignments();
    } catch (err) {
      setAssignError(err.message);
      setAssignLoading(false);
    }
  };

  return (
    <>
      {/* Inline assignments table + Assign button */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mr: 2, mt:1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',mb:1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Teachers Assigned</Typography>
          <Button variant="outlined" sx={{ borderColor: "rgba(255,118,126,0.6)", color: "#FF767E", fontWeight: 400, fontSize: "0.85rem", textTransform: "unset", height: "auto", width: "auto", borderRadius: 9, padding: "3px 14px", '&:hover': { background: "rgba(255,118,126,0.08)", borderColor: "#FF767E" } }} onClick={handleAssignClick}>
            + Assign
          </Button>
        </Box>
        {loading && <CircularProgress size={20} />}
        {error && <Alert severity="error" sx={{ py: 0 }}>{error}</Alert>}
        {!loading && assignments.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic", px: 0.5 }}>No teacher assigned</Typography>
        )}
        {!loading && assignments.length > 0 && (
          <Stack spacing={1}>
            {assignments.map((assignment, idx) => (
              <Paper
                key={assignment.id || idx}
                variant="outlined"
                sx={{ borderRadius: 2, px: 1.5, py: 0.75 }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500, minWidth: 80, flex: '0 0 auto' }}>
                    <strong>Name:</strong> {assignment.assigneeName || assignment.userName || "—"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <strong>Email:</strong> {assignment.assigneeEmail || "—"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ flex: '0 0 auto' }}>
                    {assignment.assignedAt ? <>Assigned on: {dayjs(assignment.assignedAt).format("DD/MM/YYYY")}</>  : "N/A"}
                  </Typography>
                  <IconButton size="small" onClick={() => handleRemoveClick(assignment.id)} disabled={deleting} sx={{ flex: '0 0 auto', color: rcolor }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Paper>
            ))}
          </Stack>
        )}
      </Box>

      <ConfirmDialog
        open={removeConfirmOpen}
        title="Remove Assignment"
        description="Are you sure you want to remove this teacher assignment?"
        confirmText="Remove"
        cancelText="Cancel"
        onConfirm={handleRemoveConfirm}
        onCancel={() => { setRemoveConfirmOpen(false); setRemovingId(null); }}
      />

      {/* Dialog for assigning form */}
      <Dialog open={assignDialogOpen} onClose={handleAssignDialogClose} maxWidth="xs" fullWidth>
        <DialogTitle>Assign Teacher</DialogTitle>
        <DialogContent dividers>
          <Box mb={2}>
            <Typography variant="body2" color="textSecondary">
              Enter the email and name of the teacher you want to assign this form to. Teacher will be able to complete this form.
            </Typography>
          </Box>
          <Stack spacing={1} sx={{ mt: 1 }}>
            <label style={{ color: pcolor, display: "block", fontSize: "0.9rem", marginBottom: 1 }}>Email *</label>
            <input
              type="email"
              placeholder=""
              value={assigneeEmail}
              onChange={e => setAssigneeEmail(e.target.value)}
              style={{ width: "100%", padding: 8, fontSize: 16 }}
              required
            />
            <label style={{ color: pcolor, display: "block", fontSize: "0.9rem", marginBottom: 1 }}>First and Last Name *</label>
            <input
              type="text"
              placeholder=""
              value={assigneeName}
              onChange={e => setAssigneeName(e.target.value)}
              style={{ width: "100%", padding: 8, fontSize: 16 }}
            />
            {assignError && <Alert severity="error">{assignError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAssignDialogClose} disabled={assignLoading} color="primary">Cancel</Button>
          <Button
            onClick={handleAssignSubmit}
            color="error"
            variant="contained"
            disabled={assignLoading || !assigneeEmail}
          >
            {assignLoading ? <CircularProgress size={20} /> : "Assign"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

PatientFormAssignmentList.propTypes = {
  formId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};
